import moment from 'moment';
import _ from 'lodash';
import * as XLSX from 'xlsx';


export const exportUkgReport = (data, type, section, startDate, getDoctorFullName, isAltPay, exportDates) => {
    try {
        // Build a clean exportUkgReport implementation that sources 1ST/2ND from canonical deposit maps
        const excelData = [];
        let headers = [];
        const typePrefix = type === 'ALL' ? '' : `${type} `;

        // determine effective date label
        let effectiveMoment;
        if (startDate) {
            effectiveMoment = moment(startDate);
        } else if (typeof exportDates !== 'undefined' && exportDates && exportDates.exportPayDate) {
            const parsed = moment(exportDates.exportPayDate, ['M.D.YYYY', 'MM.D.YYYY', 'M.D.YYYY']);
            effectiveMoment = parsed.isValid() ? parsed : moment();
        } else {
            effectiveMoment = moment();
        }

        // Prefer an explicit startDate label when provided from the UI (double-wrap ensures correct parsing)
        const dateLabel = startDate ? moment(moment(startDate)).format('MMMM YYYY') : effectiveMoment.format('MMMM YYYY');

        // filename: maintain legacy behavior when isAltPay + exportDates provided
        // Prefer explicit startDate-derived label when available so filename matches selected month
        const filename = isAltPay
            ? (exportDates && exportDates.exportPayDate ? (type === 'ALL' ? `UKG ${exportDates.exportPayDate}.xlsx` : `DEPOSIT ${exportDates.exportPayDate}.xlsx`) : `UKG ${section} ${typePrefix}${dateLabel}.xlsx`)
            : `UKG ${section} ${typePrefix}${dateLabel}.xlsx`;

        // ALL sheet: use canonical deposit data to preserve COMM rows and ensure 1ST/2ND parity
        if (Array.isArray(data) && data.length && type === 'ALL') {
            headers = ['PROVIDER', 'UKG ID', 'UKG CODE', 'PAY TYPE', 'SPECIALITY', 'PAY MONTH', ...(isAltPay ? [] : ['1ST DEPOSIT', '2ND DEPOSIT']), 'AMOUNT'];
            excelData.push(headers);

            const depositUtils = require('./cdpDepositUtils');
            const canonicalFirstDeposits = depositUtils.getDepositData(data, 'first', startDate, !!isAltPay) || [];
            const canonicalSecondDeposits = depositUtils.getDepositData(data, 'second', startDate, !!isAltPay) || [];

            // Grouping: REIMB aggregate at provider level (ukgid + code + ABBR).
            const makeGroupKey = (it) => {
                if (!it) return '';
                const code = (it.ukgCode || '').toString().toUpperCase();
                if (code === 'REIMB') {
                    return `${it.ukgid || ''}||${code}||${it.ABBR || ''}`;
                }
                return `${it.ukgid || ''}||${it.doctorName || ''}||${it.ukgAccountName || ''}||${it.ukgCode || ''}||${it.ABBR || ''}||${it.sageEntityName || ''}||${it.sageId || ''}`;
            };

            const accumulateEntries = (entries) => {
                const m = new Map();
                (entries || []).forEach(e => {
                    const k = makeGroupKey(e);
                    const amt = Number(e.amount || 0);
                    if (!m.has(k)) {
                        m.set(k, { ...e, amount: amt });
                    } else {
                        const ex = m.get(k);
                        ex.amount = Number(ex.amount || 0) + amt;
                        m.set(k, ex);
                    }
                });
                return m;
            };

            const firstMap = accumulateEntries(canonicalFirstDeposits);
            const secondMap = accumulateEntries(canonicalSecondDeposits);

            const combinedKeys = new Set([...firstMap.keys(), ...secondMap.keys()]);

            // Build provider lookup from original data for metadata like payType, role, payMonth
            const providerByUkgid = {};
            data.forEach(p => { if (p && p.ukgid) providerByUkgid[String(p.ukgid)] = p; });

            // Build aggregated rows keyed by PROVIDER + UKG_ID + UKG_CODE so each combination is unique
            const aggregateMap = new Map();

            Array.from(combinedKeys).forEach((key) => {
                const firstEntry = firstMap.get(key);
                const secondEntry = secondMap.get(key);
                const source = firstEntry || secondEntry;
                if (!source) return;

                const firstAmt = firstEntry ? Number(firstEntry.amount || 0) : 0;
                const secondAmt = secondEntry ? Number(secondEntry.amount || 0) : 0;
                const total = firstAmt + secondAmt;
                if (total === 0) return;

                const provider = providerByUkgid[String(source.ukgid)] || (data || []).find(d => String(d.ukgid) === String(source.ukgid)) || {};
                const providerName = typeof getDoctorFullName === 'function' ? (provider ? getDoctorFullName(provider.ABBR) : (source.doctorName || source.ABBR)) : (provider && provider.doctorName) || source.doctorName || source.ABBR || '';

                const ukgCodeNorm = ((source.ukgCode || 'COMM') + '').toString().toUpperCase();
                const mapKey = `${providerName || ''}||${source.ukgid || ''}||${ukgCodeNorm}`;

                if (!aggregateMap.has(mapKey)) {
                    aggregateMap.set(mapKey, {
                        PROVIDER: providerName || '',
                        UKG_ID: source.ukgid || '',
                        UKG_CODE: ukgCodeNorm,
                        PAY_TYPE: provider.payType || '',
                        SPECIALITY: provider.role || '',
                        PAY_MONTH: provider.payMonth || (startDate ? moment(startDate).format('MMMM YYYY') : ''),
                        FIRST: firstAmt,
                        SECOND: secondAmt,
                        AMOUNT: total
                    });
                } else {
                    const ex = aggregateMap.get(mapKey);
                    ex.FIRST = Number(ex.FIRST || 0) + firstAmt;
                    ex.SECOND = Number(ex.SECOND || 0) + secondAmt;
                    ex.AMOUNT = Number(ex.AMOUNT || 0) + total;
                    aggregateMap.set(mapKey, ex);
                }
            });

            // Convert aggregated map into rows and push into excelData
            const finalRows = Array.from(aggregateMap.values()).map(r => ({
                ...r,
                FIRST: Number(r.FIRST || 0).toFixed(2),
                SECOND: Number(r.SECOND || 0).toFixed(2),
                AMOUNT: Number(r.AMOUNT || 0).toFixed(2)
            }));

            finalRows.forEach(r => {
                const row = [];
                row.push(r.PROVIDER || '');
                row.push(r.UKG_ID || '');
                row.push(r.UKG_CODE || 'COMM');
                row.push(r.PAY_TYPE || '');
                row.push(r.SPECIALITY || '');
                row.push(r.PAY_MONTH || '');
                if (!isAltPay) {
                    row.push(r.FIRST);
                    row.push(r.SECOND);
                }
                row.push(r.AMOUNT);
                excelData.push(row);
            });

            // DEPOSIT sheets: preserve original row format used elsewhere in the repo
        } else if (Array.isArray(data) && data.length && (type === '1ST DEPOSIT' || type === '2ND DEPOSIT')) {
            headers = [
                'Company Short Name', 'Employee Id', 'Username', 'Badge Id', 'SSN#', 'Employee External Id',
                'Pay Statement #', 'Check #', 'Pay Period Start Date', 'Pay Period End Date', 'Pay Statement Type',
                'Pay Statement Type Key', 'Pay Order', 'Start Date', 'End Date', 'Start Time', 'End Time', 'E/D/T',
                'E/D/T Code', 'E/D/T Name', 'E/D/T Abbrev', 'E/D/T External ID', 'E Hours', 'E Pieces', 'E Base Rate',
                'E Shift', 'E/D/T Amount', 'WC Code', 'T EE Gross Wages', 'T ER Gross Wages', 'T EE Gross Subject Wages',
                'T ER Gross Subject Wages', 'T EE Subject Wages', 'T ER Subject Wages', 'D/T Employer Amt', 'CC1', 'CC2',
                'CC3', 'CC4', 'CC5', 'D Vendor', 'Mode', 'Count Zeroes'
            ];
            excelData.push(headers);

            data.forEach((item) => {
                const row = [];
                const doctorName = typeof getDoctorFullName === 'function' ? getDoctorFullName(item.ABBR) : (item.doctorName || '');
                const ukgCode = item.ukgCode ? (String(item.ukgCode).includes('W2') ? String(item.ukgCode).replace('W2', 'COMM') : item.ukgCode) : '';

                row.push('6174247');
                row.push(item.ukgid || '');
                row.push(doctorName || '');
                row.push('');
                row.push('');
                row.push('');
                row.push('');
                row.push('');
                row.push('');
                row.push('');
                row.push('REGULAR');
                row.push('');
                row.push('');
                row.push('');
                row.push('');
                row.push('');
                row.push('');
                row.push('E');
                row.push(ukgCode);
                row.push('');
                row.push('');
                row.push('');
                row.push('');
                row.push('');
                row.push('');
                row.push('');

                const amount = item.amount;
                let formattedAmount = '';
                if (amount !== null && amount !== undefined && amount !== '') {
                    const numValue = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
                    if (!isNaN(numValue)) {
                        formattedAmount = numValue.toFixed(2);
                    }
                }
                row.push(formattedAmount);

                // fill remaining columns with blanks and insert sageId where expected
                for (let i = 0; i < 8; i++) row.push('');
                row.push(item.sageId || '');
                for (let i = 0; i < 9; i++) row.push('');

                excelData.push(row);
            });
        }

        // Create workbook and download
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(excelData);
        XLSX.utils.book_append_sheet(workbook, worksheet, `UKG ${type} ${dateLabel}`);

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const fileBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(fileBlob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
    } catch (error) {
        console.error('UKG export failed:', error);
        throw error;
    }
};

export const getAuditNum = (id) => {
    const padding = `${id}`.length < 4 ? [...new Array(4 - `${id}`.length)].map(() => '0').join('') : '';
    return `A${padding}${id}`;
}

export const getPaddedPatNum = (patNum) => {
    const padding = `${patNum}`.length < 5 ? [...new Array(5 - `${patNum}`.length)].map(() => '0').join('') : '';
    return `${padding}${patNum}`;
}

export const dateFormats = [
    moment.ISO_8601,
    'MM-DD-YYYY',
    'YYYY-MM-DD',
    'DD-MM-YYYY',
    'YYYY/MM/DD',
    'YYYYMMDD',
    'MM/DD/YYYY',
    'M/D/YYYY HH:mm',
    'YYYYMMDD HHmmss',
    'MM/D/YYYY',
    'M/D/YYYY'
]
export const calculateAge = (birthDateMoment, specificDateMoment) => {
    return specificDateMoment.diff(birthDateMoment, 'years');
};

export const formatCurrency = (value) => {
    const floatValue = Number(value);
    if (isNaN(floatValue)) return '$0.00'; // handle invalid input

    return floatValue
        .toFixed(2)                     // force 2 decimals
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",") // add commas
        .replace(/^(-?)(\d.*)/, "$1$$$2");     // prepend $
};

export const unFormatCurrency = (value) => {
    if (value) {
        value = value.toString()
        const stringValue = ((value).replace(',', '')).replace('$', '');
        return parseFloat(stringValue)
    } else {
        return 0
    }
}

export const groupBy = (objectArray = [], property) => {
    return objectArray.reduce((acc, obj) => {
        const key = obj && obj[property] !== undefined && obj[property] !== null ? obj[property] : '';
        if (!acc[key]) acc[key] = [];
        acc[key].push(obj);
        return acc;
    }, {});
};

// Sort an array of objects by a date property (string or Date). Returns a new sorted array.
export const sortByDate = (arr = [], dateProp = 'date') => {
    if (!Array.isArray(arr)) return [];
    return arr.slice().sort((a, b) => {
        const A = a && a[dateProp] ? new Date(a[dateProp]) : new Date(0);
        const B = b && b[dateProp] ? new Date(b[dateProp]) : new Date(0);
        return A - B;
    });
};

// Comparator function usable in Array.prototype.sort(compareByDate)
export const compareByDate = (a, b, prop = 'date') => {
    const A = a && a[prop] ? new Date(a[prop]) : new Date(0);
    const B = b && b[prop] ? new Date(b[prop]) : new Date(0);
    return A - B;
};

// Format minutes (number) into H:mm or mm format depending on magnitude
export const formatMinutes = (minutes) => {
    if (minutes === null || minutes === undefined || isNaN(Number(minutes))) return '';
    const mins = Math.floor(Number(minutes));
    const sign = mins < 0 ? '-' : '';
    const abs = Math.abs(mins);
    const hours = Math.floor(abs / 60);
    const rem = abs % 60;
    if (hours > 0) return `${sign}${hours}:${String(rem).padStart(2, '0')}`;
    return `${sign}${rem}`;
};

// Sum numeric values of a key in an array of objects
export const sumKeyValueOfArray = (arr = [], key) => {
    if (!Array.isArray(arr)) return 0;
    return arr.reduce((acc, item) => acc + (Number(item && item[key]) || 0), 0);
};

export const getDerivedStateFromProps = (nextProps, prevState) => {
    let data = {};
    if (nextProps.user !== prevState.user) {
        data = { ...data, user: nextProps.user };
    }
    if (nextProps.user !== prevState.user && nextProps.user && nextProps.user.scheduleDoctors) {
        const obj = nextProps.user.scheduleDoctors
        const scheduleDoctorsArray = Object.keys(obj).map(key => obj[key]);
        const options = _.sortBy(scheduleDoctorsArray, "firstName").map(item => ({
            key: item.UserID,
            value: item.UserID,
            text: `${item.firstName} ${item.lastName}`
        }));
        data = {
            ...data,
            providers: [{ key: 'select-all', value: null, text: 'Select All' }, ...options]
        };
    }

    return data;
}

export const tryParseJson = (jsonString) => {
    try {
        return JSON.parse(jsonString);
    } catch (e) {
        console.error(e);
    }
    return undefined;
}


export const boolToInt = val => +!!val;

export const getUniqueArrObj = (arr, comp) => {

    const unique = arr
        .map(e => e[comp])

        .map((e, i, final) => final.indexOf(e) === i && i)

        .filter(e => arr[e]).map(e => arr[e]);

    return unique;
}

export const buildPracticeLists = (data = []) => {
    const practiceArray = [];
    const practiceList = [];
    const practices = [];
    const locationList = [];

    data.forEach((res) => {
        practiceArray.push(res);

        practiceList.push({
            key: res.id,
            value: res.practice,
            text: `${res.practice} (${res.practiceabbr})`,
        });

        const simpleObj = {
            key: res.id,
            value: res.practice,
            text: res.practice,
        };

        practices.push(simpleObj);
        locationList.push(simpleObj);
    });

    return { practiceArray, practiceList, practices, locationList };
};

export const getDoctorWorkHistoryData = (docID, doctorWorkHistory) => {
    return doctorWorkHistory && doctorWorkHistory.filter(sch => {
        return (Number(sch.id) === docID)
    });
}

export const getNearestWorkDays = (workHistory = []) => {
    return workHistory.reduce(
        (acc, wh) => {
            if (wh.lastWorkDay !== null && wh.lastWorkDay !== undefined) {
                const last = Number(wh.lastWorkDay);
                if (Number.isFinite(last) && last > 0 && (acc.lastAppt === null || last > acc.lastAppt)) {
                    acc.lastAppt = last;
                }
            }
            if (wh.nextWorkDay !== null && wh.nextWorkDay !== undefined) {
                const next = Number(wh.nextWorkDay);
                if (Number.isFinite(next) && next > 0 && (acc.nextAppt === null || next < acc.nextAppt)) {
                    acc.nextAppt = next;
                }
            }
            return acc;
        },
        { lastAppt: null, nextAppt: null }
    );
};

export const getDoctorDetails = (abbr, user) => {
    if (!user?.scheduleDoctors) return [];

    const target = abbr?.toLowerCase();

    const allDentistListArray = Object.values(user.scheduleDoctors).map(dr => ({
        ...dr,
        abbr: dr.abbr?.toLowerCase() || dr.abbr
    }));

    return allDentistListArray.filter(d => d.abbr === target);
};

export const dynamicDentistList = (user, dentistTypes, listName, abbrAsValue = false) => {
    let dentistAbbrObj = {};
    let allDentistListArray;
    let dentistList = [];
    let dentistOptions = [];
    let dentistArray = []

    allDentistListArray = Object.values(user?.scheduleDoctors ?? {}).map(drItem => {
        drItem.abbr = drItem.abbr?.toLowerCase();
        return drItem;
    });

    // A provider can hold permissions under more than one of dentistTypes, so merge/dedupe
    // across them by doctorId rather than processing each role's bucket separately.
    if (user?.drList && dentistTypes?.length > 0) {
        mergeDrListByRoles(user.drList, dentistTypes, (drItem) => drItem.doctorId).forEach(({abbr, drItem}) => {
            dentistAbbrObj[drItem.doctorId] = drItem.abbr ? drItem.abbr.toLowerCase() : null;
            dentistList[drItem.doctorId] = drItem;

            if ((drItem.firstName || drItem.lastName) && drItem.role !== 'Admin') {
                dentistOptions.push({
                    key: drItem.doctorId,
                    value: abbrAsValue ? abbr : drItem.doctorId,
                    text: `${drItem.firstName} ${drItem.lastName}(${abbr || ""})`,
                    title: drItem.lastName,
                    className: 'primary'
                });
            }

            if (drItem.abbr) {
                drItem.abbr = drItem.abbr.toLowerCase();
            }
            dentistArray.push(drItem);
        });
    }

    dentistOptions = _.sortBy(dentistOptions, ['title']);
    dentistOptions.unshift({ key: false, value: false, text: 'All' });
    dentistList = { dentistOptions, dentistAbbrObj, dentistList, allDentistListArray, dentistArray };
    return dentistList[listName];
}

// A provider can hold permissions under more than one drList role id (e.g. dentist
// and hygienist), so callers merging multiple role buckets must dedupe across them -
// concatenating each role's bucket separately re-lists the same provider once per role.
export const mergeDrListByRoles = (drList, roleIds, getDedupKey = (drItem, abbr) => abbr) => {
    const seen = new Set();
    const merged = [];
    (roleIds || []).forEach(roleId => {
        const roleDrList = drList && drList[roleId];
        if (!roleDrList) return;
        Object.keys(roleDrList).forEach(abbr => {
            const drItem = roleDrList[abbr];
            const dedupKey = getDedupKey(drItem, abbr);
            if (seen.has(dedupKey)) return;
            seen.add(dedupKey);
            merged.push({ abbr, drItem });
        });
    });
    return merged;
};

export const removeDuplicatesFromArray = (array, property) => {
    const seen = new Set();
    return array.filter(object => {
        const isDuplicate = seen.has(object[property]);
        seen.add(object[property]);
        return !isDuplicate;
    })
}

export function getPercentageOfBillableCare(basePay, Bcare, financialNotes) {
    return (100 * (basePay / (Bcare + financialNotes)))
}

export const transformWriteOffs = (_obj) => {
    if (!_obj) return _obj;
    const obj = { ..._obj };
    obj.price *= -1;
    Object.keys(obj.data).forEach(key => {
        obj.data[key].price *= -1;
        Object.keys(obj.data[key].data).forEach(key_1 => {
            obj.data[key].data[key_1]?.forEach((v, i) => obj.data[key].data[key_1][i].WriteOff *= -1);
        })
    });
    return obj;
}

export function base64ToBlob(base64Data, contentType = 'image/jpeg') {
    const base64String = base64Data.includes(',')
        ? base64Data.split(',')[1]
        : base64Data;

    const byteCharacters = atob(base64String);
    const byteArrays = [];

    for (let i = 0; i < byteCharacters.length; i += 512) {
        const slice = byteCharacters.slice(i, i + 512);
        const byteNumbers = new Array(slice.length);
        for (let j = 0; j < slice.length; j++) {
            byteNumbers[j] = slice.charCodeAt(j);
        }
        byteArrays.push(new Uint8Array(byteNumbers));
    }

    return new Blob(byteArrays, { type: contentType });
}